/** Job rendering for UGC Studio — status badge, active card, history grid. */
import { useTranslation } from "react-i18next";
import { Download, Loader2, Play } from "lucide-react";
import type { UgcJob } from "./api";
import { RemixAudioPanel } from "./audio-studio-panel";
import { useUgcLight } from "./ugc-theme";

export function JobCard({ job, highlighted }: { job: UgcJob; highlighted?: boolean }) {
  const L = useUgcLight();
  return (
    <div
      className={`max-w-5xl mx-auto mt-6 rounded-2xl overflow-hidden border p-4 ${
        highlighted
          ? "border-[#ef3e29]/40 bg-[#ef3e29]/[0.04]"
          : L
            ? "border-gray-200 bg-white"
            : "border-white/[0.08] bg-white/[0.02]"
      }`}
    >
      <div className="flex items-center gap-3 mb-3">
        <StatusBadge status={job.status} />
        <AudioSourceBadge job={job} />
        <div className={`text-sm truncate flex-1 ${L ? "text-gray-700" : "text-white/80"}`}>
          {job.prompt}
        </div>
        <div className={`text-xs ${L ? "text-gray-400" : "text-white/40"}`}>
          {job.videoModel} · {job.durationSeconds}s
        </div>
      </div>
      <JobBody job={job} />
      {job.status === "completed" ? <JobDownloads job={job} /> : null}
      {job.status === "completed" && job.videoUrl ? (
        <div className="mt-3">
          <RemixAudioPanel
            jobId={job.id}
            avatarId={job.avatarId}
            format={job.format}
            durationSeconds={job.durationSeconds}
          />
        </div>
      ) : null}
    </div>
  );
}

interface DownloadEntry {
  url: string;
  label: string;
  filename: string;
  hint?: string;
}

function JobDownloads({ job }: { job: UgcJob }) {
  const L = useUgcLight();
  const { t } = useTranslation();
  const entries: DownloadEntry[] = [];
  if (job.videoUrl) {
    entries.push({
      url: job.videoUrl,
      label: t("ugcStudio.jobs.finalVideo"),
      filename: `ugc-${job.id}.mp4`,
      hint: t("ugcStudio.jobs.finalVideoHint"),
    });
  }
  if (job.seedanceVideoUrl) {
    entries.push({
      url: job.seedanceVideoUrl,
      label: t("ugcStudio.jobs.seedanceVideo"),
      filename: `ugc-${job.id}-seedance.mp4`,
      hint: t("ugcStudio.jobs.seedanceVideoHint"),
    });
  }
  if (job.audioUrl) {
    entries.push({
      url: job.audioUrl,
      label: t("ugcStudio.jobs.audioTts"),
      filename: `ugc-${job.id}.mp3`,
      hint: t("ugcStudio.jobs.audioTtsHint"),
    });
  }
  if (entries.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {entries.map((e) => (
        <a
          key={e.filename}
          href={e.url}
          download={e.filename}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition ${L ? "bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200 hover:border-gray-300" : "bg-white/[0.04] border-white/[0.08] text-white/80 hover:bg-white/[0.08] hover:border-white/20"}`}
          title={e.hint}
        >
          <Download className="w-3.5 h-3.5 opacity-70" />
          {e.label}
        </a>
      ))}
    </div>
  );
}

function JobBody({ job }: { job: UgcJob }) {
  const L = useUgcLight();
  const { t } = useTranslation();
  if (job.status === "completed" && job.videoUrl) {
    return (
      <video src={job.videoUrl} controls className="w-full max-h-[520px] rounded-lg bg-black" />
    );
  }
  if (job.status === "failed") {
    return (
      <div className={`text-sm ${L ? "text-red-600" : "text-red-300"}`}>
        {job.errorMessage ?? t("ugcStudio.jobs.generationError")}
      </div>
    );
  }
  return (
    <div className={`flex items-center gap-3 text-sm ${L ? "text-gray-500" : "text-white/60"}`}>
      <Loader2 className="w-4 h-4 animate-spin" />
      {t("ugcStudio.jobs.generatingProgress", { progress: job.progress })}
    </div>
  );
}

const MAX_RECENT_JOBS = 8;

interface JobListProps {
  jobs: UgcJob[];
  activeId: string | null;
  onOpen: (id: string) => void;
}

export function JobList({ jobs, activeId, onOpen }: JobListProps) {
  const L = useUgcLight();
  const { t } = useTranslation();
  if (jobs.length === 0) return null;
  return (
    <div className="max-w-5xl mx-auto mt-8">
      <div
        className={`text-xs uppercase tracking-wider mb-2 ${L ? "text-gray-400" : "text-white/40"}`}
      >
        {t("ugcStudio.jobs.recentGenerations")}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {jobs.slice(0, MAX_RECENT_JOBS).map((j) => (
          <button
            key={j.id}
            onClick={() => onOpen(j.id)}
            className={`text-left rounded-lg border p-3 transition ${
              activeId === j.id
                ? "border-[#ef3e29]/40 bg-[#ef3e29]/[0.04]"
                : L
                  ? "border-gray-200 bg-white hover:border-gray-300"
                  : "border-white/[0.06] bg-white/[0.02] hover:border-white/15"
            }`}
          >
            <StatusBadge status={j.status} />
            <div className={`mt-1 text-xs line-clamp-2 ${L ? "text-gray-700" : "text-white/80"}`}>
              {j.prompt}
            </div>
            <div className={`mt-1 text-[11px] ${L ? "text-gray-400" : "text-white/40"}`}>
              {j.videoModel}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

const STATUS_KEY: Record<UgcJob["status"], string> = {
  queued: "ugcStudio.jobs.statusQueued",
  generating: "ugcStudio.jobs.statusGenerating",
  completed: "ugcStudio.jobs.statusCompleted",
  failed: "ugcStudio.jobs.statusFailed",
};

const STATUS_COLOR_DARK: Record<UgcJob["status"], string> = {
  queued: "bg-white/10 text-white/70",
  generating: "bg-yellow-500/15 text-yellow-200",
  completed: "bg-emerald-500/15 text-emerald-200",
  failed: "bg-red-500/15 text-red-200",
};

const STATUS_COLOR_LIGHT: Record<UgcJob["status"], string> = {
  queued: "bg-gray-100 text-gray-600",
  generating: "bg-yellow-100 text-yellow-700",
  completed: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700",
};

function StatusBadge({ status }: { status: UgcJob["status"] }) {
  const L = useUgcLight();
  const { t } = useTranslation();
  const color = L ? STATUS_COLOR_LIGHT[status] : STATUS_COLOR_DARK[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] ${color}`}>
      {status === "completed" ? <Play className="w-3 h-3" /> : null}
      {t(STATUS_KEY[status])}
    </span>
  );
}

const AUDIO_SOURCE_LABEL: Record<NonNullable<UgcJob["audioMode"]>, string> = {
  native: "Audio: Seedance",
  overlay: "Audio: ElevenLabs (overlay)",
  lipsync: "Audio: ElevenLabs (lip-sync)",
  voiceover: "Audio: ElevenLabs (voiceover)",
};

function AudioSourceBadge({ job }: { job: UgcJob }) {
  const mode = job.audioMode ?? "native";
  const isElevenLabs = mode !== "native";
  // Twardy dowód że audio z ElevenLabs naprawdę zostało wygenerowane —
  // backend zapisuje audioR2Key tylko gdy faktycznie wgrał plik mp3 z TTS.
  const verified = isElevenLabs && !!job.audioR2Key;
  const palette = !isElevenLabs
    ? "bg-white/10 text-white/60"
    : verified
      ? "bg-emerald-500/15 text-emerald-200"
      : "bg-amber-500/15 text-amber-200";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] ${palette}`}
      data-testid="audio-source-badge"
    >
      {AUDIO_SOURCE_LABEL[mode]}
      {verified ? " ✓" : ""}
    </span>
  );
}
